/**
 * useSidebarNavigation – Hook build sidebar menu từ permissions
 * v4: Loại bỏ Context filter, chỉ dùng Phân quyền (RBAC)
 */
import { useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { useAdminRolePreviewContext } from '@/core/roles/AdminRolePreviewContext';
import { useProjectContext } from '@/core/projects/ProjectContext';
import {
  navigationConfig,
  roleLandingPageMap,
  roleLabels,
  roleBadgeColors,
} from '@/config/navigation.config';
import { getModuleForNavItem } from '@/config/nav-module-map.config';
import { useMergedPermissions } from './useMergedPermissions';
import { permissionMatrixConfig } from '@/config/permission-matrix.config';
import type { UserRole } from '@/config/navigation.config';
import type { Module } from '@/config/permission-matrix.config';

const PINNED_BOTTOM_KEYS = new Set<string>();

export interface SidebarRenderItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  badge?: string;
  isActive: boolean;
}

export interface SidebarRenderGroup {
  id: string;
  label: string;
  items: SidebarRenderItem[];
}

export interface UseSidebarNavigationReturn {
  groups: SidebarRenderGroup[];
  pinnedItems: SidebarRenderItem[];
  activeGroupId: string | null;
  landingPage: string;
  roleLabel: string;
  roleColor: { bg: string; text: string; border: string };
  isLoading: boolean;
  refresh: () => void;
  activeRoleCount: number;
}

export function useSidebarNavigation(): UseSidebarNavigationReturn {
  const location = useLocation();
  const { user, allRoleCodes, isMultiRole } = useCurrentUser();
  const { previewRole, isPreviewMode } = useAdminRolePreviewContext();
  const { activeProjectId } = useProjectContext();
  const { can, isLoading: permsLoading, refresh } = useMergedPermissions();

  const realRole = user.roleCode;
  const effectiveRole: UserRole = isPreviewMode && previewRole ? previewRole : realRole;

  // Build filtered groups – union theo TẤT CẢ roles
  const { groups, pinnedItems, activeGroupId } = useMemo(() => {
    const mainGroups: SidebarRenderGroup[] = [];
    const pinned: SidebarRenderItem[] = [];
    let activeGroup: string | null = null;
    const currentPath = location.pathname;

    navigationConfig.forEach((group) => {
      const visibleItems: SidebarRenderItem[] = [];

      group.items.forEach((item) => {
        let isVisible = false;

        if (isPreviewMode && previewRole) {
          const moduleKey = getModuleForNavItem(item.key);
          if (moduleKey) {
            const perms = permissionMatrixConfig[previewRole]?.[moduleKey as Module] ?? [];
            isVisible = perms.includes('view');
          } else {
            isVisible = item.roles.includes(previewRole);
          }
        } else {
          const moduleKey = getModuleForNavItem(item.key);
          const hasRole = allRoleCodes.some(r => item.roles.includes(r));
          const isSuperAdmin = allRoleCodes.includes('super_admin');

          if (isSuperAdmin) {
            isVisible = true;
          } else if (moduleKey) {
            isVisible = hasRole && can(moduleKey as Module, 'view');
          } else {
            isVisible = hasRole;
          }
        }

        if (!isVisible) return;

        let path = item.path;
        const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;
        
        // Paths that should NOT be project-scoped
        const globalExclusions = [
          '/maintenance/contracts',
          '/maintenance/zones',
          '/maintenance/assets',
          '/maintenance/teams',
          '/maintenance/workload',
          '/maintenance/plans',
          '/maintenance/schedules',
          '/maintenance/work-orders',
          '/maintenance/qa',
          '/maintenance/issues',
          '/maintenance/cost-tracking',
          '/maintenance/task-catalog',
          '/maintenance/logs'
        ];
        
        const isGlobalPath = globalExclusions.includes(normalizedPath);
        
        // Chỉ replace nếu là path maintenance, chưa có projectId, và không nằm trong danh sách loại trừ
        if (activeProjectId && 
            path.startsWith('/maintenance/') && 
            !path.match(/^\/maintenance\/[a-f0-9-]{36}\//i) && 
            !isGlobalPath) {
          path = path.replace('/maintenance/', `/maintenance/${activeProjectId}/`);
        }

        const isActive =
          currentPath === path ||
          (path !== '/' && currentPath.startsWith(path));

        const renderItem: SidebarRenderItem = {
          id: item.key,
          label: item.label,
          path,
          icon: item.icon,
          badge: item.badge,
          isActive,
        };

        if (PINNED_BOTTOM_KEYS.has(item.key)) {
          pinned.push(renderItem);
        } else {
          visibleItems.push(renderItem);
        }

        if (isActive) {
          activeGroup = group.key;
        }
      });

      if (visibleItems.length > 0) {
        mainGroups.push({
          id: group.key,
          label: group.label,
          items: visibleItems,
        });
      }
    });

    return { groups: mainGroups, pinnedItems: pinned, activeGroupId: activeGroup };
  }, [location.pathname, activeProjectId, allRoleCodes, isPreviewMode, previewRole, can]);

  const landingPage = roleLandingPageMap[effectiveRole] ?? '/';
  const roleLabel = roleLabels[effectiveRole] ?? effectiveRole;
  const roleColor = roleBadgeColors[effectiveRole] ?? { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };

  return {
    groups,
    pinnedItems,
    activeGroupId,
    landingPage,
    roleLabel,
    roleColor,
    isLoading: permsLoading,
    refresh,
    activeRoleCount: isPreviewMode ? 1 : (isMultiRole ? allRoleCodes.length : 1),
  };
}
